import { anyReallyAny } from "@cervval.js/common/tools";
import Quill, { QuillOptions } from "quill";
import QuillImageDropAndPaste from "quill-image-drop-and-paste";
import { ToolbarProps } from "quill/modules/toolbar";

export type RichTextConfiguration = Partial<QuillOptions>;

/** Pre-configs available for RichTextEditor toolbar */
export type BaseRichTextConfigurationsKey = "basic" | "minimal" | "advanced" | "advancedToolbar";

export type BaseRichTextToolbarConfigurations = { [key in BaseRichTextConfigurationsKey]: ToolbarProps }
export type BaseQuillConfigurations = { [key in BaseRichTextConfigurationsKey]: RichTextConfiguration }

// Icons for custom buttons undo/redo
const quillIcons: anyReallyAny = Quill.import("ui/icons");
quillIcons.undo = "<i class='fas fa-rotate-left'></i>";
quillIcons.redo = "<i class='fas fa-rotate-right'></i>";

/**
 * Options to add to display undo/redo buttons inside toolbar
 * (should be append in options.configuration.modules.toolbar.container)
 */
export const toolbarHistoryButtonsOptions = ["undo", "redo"];

/**
 * Config of history for toolbar of richTextEditor
 * @see https://quilljs.com/docs/modules/toolbar
 */
export const toolbarHistoryOptions: ToolbarProps = {
    handlers: {
        undo() { this.quill.history.undo(); },
        redo() { this.quill.history.redo(); }
    },
    container: [
        toolbarHistoryButtonsOptions
    ]
};

export const BaseRichTextToolbarConfigurationsValues: BaseRichTextToolbarConfigurations = {
    // The basic config of Quill editor
    basic: {
        ...toolbarHistoryOptions,
        container: [
            ["bold", "italic", "underline"],
            toolbarHistoryButtonsOptions,
            [{ "color": [] }],
            [{ "header": [false, 1, 2, 3, 4, 5, 6] }],// Heading
            [{ "align": [] }],
            [{ "indent": "-1" }, { "indent": "+1" }]// outdent/indent
        ]
    },
    // A text editor with the minimal text edition requirements
    minimal: {
        container: [
            ["bold", "italic", "underline"]
        ]
    },
    advanced: {
        ...toolbarHistoryOptions,
        container: [
            ["bold", "italic", "underline"],
            [{ "color": [] }, { "background": [] }],
            ["link", "image"],
            ["undo", "redo"],
            [{ "header": [false, 1, 2, 3, 4, 5, 6] }],// Heading
            [{ "align": [] }],
            [{ "indent": "-1" }, { "indent": "+1" }]          // outdent/indent
        ]
    },
    /** Toolbar with major options available enable */
    advancedToolbar: {
        ...toolbarHistoryOptions,
        container: [
            ["bold", "italic", "underline"],
            [{ "color": [] }, { "background": [] }],
            ["link", "image"],
            ["undo", "redo"],
            [{ "header": [false, 1, 2, 3, 4, 5, 6] }],// Heading
            [{ "align": [] }],
            [{ "font": [] }],
            ["blockquote", "code-block"],
            [{ "list": "ordered" }, { "list": "bullet" }],
            [{ "indent": "-1" }, { "indent": "+1" }]  // outdent/indent

        ]
    }
};

/** Base config of richText editor */
export const richTextBaseConfig: RichTextConfiguration = {
    theme: "snow",
    modules: {
        toolbar: BaseRichTextToolbarConfigurationsValues["basic"],
        history: {
            delay: 1000,
            maxStack: 100,
            userOnly: true
        },
        clipboard: {
            matchVisual: false
        }
    }
};

// Register DropAndPaste module
Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);

//#region QUILL CLASSES REPLACEMENT
Quill.debug("error"); // hide "Overwriting …" in console

// Get "style" attributors of Quill, used to set "style" attribute for blocks instead of quill's classes
const AlignStyle = Quill.import("attributors/style/align") as anyReallyAny;
const DirectionStyle = Quill.import("attributors/style/direction") as anyReallyAny;
const FontStyle = Quill.import("attributors/style/font") as anyReallyAny;
Quill.register(AlignStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);

//
// 1) CODE inline  -> <code style="...">...</code>
//
const InlineCode = Quill.import("formats/code") as anyReallyAny;
class InlineCodeStyle extends InlineCode {
    public static blotName = "code";
    public static tagName = "CODE";

    /** @inheritdoc */
    public static create(value?: anyReallyAny): HTMLElement {
        const node = super.create(value) as HTMLElement;
        node.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace";
        node.style.backgroundColor = "#161616ff";
        node.style.color = "#f3f3f3ff";
        node.style.border = "solid #cfcfcfff 1px";
        node.style.padding = "0.75em 1em";
        return node;
    }
}
Quill.register(InlineCodeStyle, true);

//
// 2) CODE-BLOCK  -> <pre style="..."><code>...</code></pre>
//
const CodeBlock = Quill.import("formats/code-block") as anyReallyAny;
class CodeBlockStyle extends CodeBlock {
    public static blotName = "code-block";
    public static tagName = "PRE";

    /** @inheritdoc */
    public static create(value?: anyReallyAny): HTMLElement {
        const node = super.create(value) as HTMLElement;
        node.removeAttribute("class");
        node.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace";
        node.style.backgroundColor = "#161616ff";
        node.style.color = "#f3f3f3ff";
        node.style.border = "solid #cfcfcfff 1px";
        node.style.padding = "0.75em 1em";
        return node;
    }
}
Quill.register(CodeBlockStyle, true);

//
// 3) QUOTE (blockquote) -> <blockquote style="...">...</blockquote>
//
const Blockquote = Quill.import("formats/blockquote") as anyReallyAny;
class BlockQuoteStyle extends Blockquote {
    public static blotName = "blockquote";
    public static tagName = "BLOCKQUOTE";

    /** @inheritdoc */
    public static create(value?: anyReallyAny): HTMLElement {
        const node = super.create(value) as HTMLElement;
        node.removeAttribute("class");
        node.style.margin = "0 0 1em 0";
        node.style.padding = "0.25em 1em";
        node.style.color = "#555";
        node.style.backgroundColor = "#f5f5f5";
        return node;
    }
}
Quill.register(BlockQuoteStyle, true);

// Register list formatter, don't use Quill's classes
const ListItem = Quill.import("formats/list") as anyReallyAny;
class ListItemStyle extends ListItem {
    public static blotName = "list";
    public static tagName = "LI";

    /** @inheritdoc */
    public static create(value: anyReallyAny): HTMLElement {
        const node = super.create(value) as HTMLElement;
        node.removeAttribute("class");

        // value = "ordered" | "bullet" | "checked" | "unchecked"
        switch (value) {
            case "ordered":
                node.style.listStyleType = "decimal";
                break;
            case "bullet":
                node.style.listStyleType = "disc";
                break;
            case "checked":
            case "unchecked":
            default:
                node.style.listStyleType = "disc";
        }

        return node;
    }
}
Quill.register(ListItemStyle, true);


//#endregion QUILL CLASSES REPLACEMENT